import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { gungComparatorHelper, GungImageType, GungImageUrlService } from 'gung-common';
import { ConfigBaseFilter, ConfigService, ListLayout, ListLayoutMultipleComponent, ListSortOption, PimTemplateProperties, SkeletonLoaderLayout } from 'gung-list';
import { BehaviorSubject, catchError, filter, map, Observable, of, Subject, switchMap, tap } from 'rxjs';
import { ConceptGridCardV2Component } from '../../components/concept-grid-card-v2/concept-grid-card-v2.component';
import { ProductGridCardV2Component } from '../../components/product-grid-card-v2/product-grid-card-v2.component';
import { ProductGridViewV2Component } from '../../components/product-grid-view-v2/product-grid-view-v2.component';
import { GridViewProductCard } from '../../components/product-grid-view/product-grid-view.component';
import { ProductListViewV2Component } from '../../components/product-list-view-v2/product-list-view-v2.component';
import { Product, ProductConcept, ProductExtended } from '../../models';
import { NavigationConfig } from '../../models/navigation-config';
import { Assortment, AssortmentService } from '../assortment.service';
import { AuthService } from '../auth/auth.service';
import { BaseViewConfigService, CurrentAssortmentService } from '../base-view-config/base-view-config.service';
import { CloudPimFiltersService } from '../cloud-pim-filters/cloud-pim-filters.service';
import { GungFlowService } from '../gung-flow/gung-flow.service';
import { MetadataService } from '../metadata/metadata.service';
import { ProductSortService } from '../product-sort/product-sort.service';
import { ProductService } from '../products/product.service';
import { ProductListSettingsService } from '../product-list-settings/product-list-settings.service';

@Injectable({
  providedIn: 'root'
})
export class ProductListV2ConfigService implements ConfigService<Product> {
  protected productService = inject(ProductService);
  protected authService = inject(AuthService);
  protected productSortService = inject(ProductSortService);
  protected gungFlowService = inject(GungFlowService);
  protected metadataService = inject(MetadataService);
  protected assortmentService = inject(AssortmentService);
  protected cloudPimFiltersService = inject(CloudPimFiltersService);
  protected currentAssortmentService = inject(CurrentAssortmentService);
  protected productListSettingsService = inject(ProductListSettingsService);
  protected gungImageUrlService = inject(GungImageUrlService);
  protected translateService = inject(TranslateService);
  protected baseViewConfigService = inject(BaseViewConfigService);
  protected environment = inject<NavigationConfig>('environment' as any);

  assortmentSubject: Subject<Assortment> = new BehaviorSubject(null);
  assortmentBannerEnabled = this.productListSettingsService.assortmentBannerEnabled ?? true;
  fetchProductsExpanded = this.productListSettingsService.fetchProductsExpanded ?? false;
  topFilter = this.productListSettingsService.topFilter ?? (this.environment?.standardErp || false);
  searchDisabled = this.productListSettingsService.searchDisabled ?? false;
  // Layout isn't known yet before the assortment loads (getLayouts() depends on it), so default
  // to the grid skeleton shown while lib-product-list-v2 waits for assortmentLoaded.
  skeletonLoaderLayout: SkeletonLoaderLayout = 'grid';
  dynamicColumns: PimTemplateProperties[];
  currentLang = this.translateService.currentLang || 'se';

  get infiniteScroll(): boolean {
    return this.productListSettingsService.infiniteScroll ?? this.baseViewConfigService.productListInfiniteScroll;
  }

  getFilters(): ConfigBaseFilter<Product>[] {
    return this.cloudPimFiltersService.getFilters(
      this.currentAssortmentService.currentAssortment,
      undefined,
      undefined,
      this.productListSettingsService.hideUndefinedFilters
    );
  }

  getSortOptions(): ListSortOption<Product>[] {
    if (this.environment.standardErp) {
      return [
        {
          getLabel: () => 'NAME_A_Z',
          getComparator: () => (item1: Product, item2: Product) => gungComparatorHelper(item1?.name, item2?.name, 1)
        },
        {
          getLabel: () => 'NAME_Z_A',
          getComparator: () => (item1: Product, item2: Product) => gungComparatorHelper(item1?.name, item2?.name, -1)
        }
      ];
    }
    return [
      {
        getLabel: () => 'POPULAR',
        getComparator: () => (item1: Product, item2: Product) =>
          gungComparatorHelper(item1?.extra?.pim?.sequence, item2?.extra?.pim?.sequence, -1)
      },
      {
        getLabel: () => 'NAME_ASC',
        getComparator: () => (item1: Product, item2: Product) => gungComparatorHelper(item1?.name, item2?.name, 1)
      },
      {
        getLabel: () => 'NAME_DESC',
        getComparator: () => (item1: Product, item2: Product) => gungComparatorHelper(item1?.name, item2?.name, -1)
      },
      {
        getLabel: () => 'ITEM_NO_ASC',
        getComparator: () => (item1: Product, item2: Product) => gungComparatorHelper(item1?.id, item2?.id, 1)
      },
      {
        getLabel: () => 'ITEM_NO_DESC',
        getComparator: () => (item1: Product, item2: Product) => gungComparatorHelper(item1?.id, item2?.id, -1)
      }
    ];
  }

  getBatchSizes(): number[] {
    if (this.productListSettingsService.batchSizes != null) {
      return this.productListSettingsService.batchSizes;
    }
    if (this.environment?.standardErp) {
      return [24];
    }
    return [24, 48];
  }

  getLayouts(): ListLayout<Product>[] {
    const currentAssortment = this.currentAssortmentService.currentAssortment;
    if (currentAssortment && currentAssortment.extra.pim && currentAssortment.extra.pim.showAsList) {
      return [
        {
          getIconClass: () => 'fa fa-list',
          getListItemComponent: () => ProductListViewV2Component,
          getListLayoutComponent: () => ListLayoutMultipleComponent,
          getName: () => 'ProductList',
          getSkeletonLayout: () => 'table'
        },
        {
          getIconClass: () => 'fa fa-th',
          getListItemComponent: () => ProductGridViewV2Component,
          getListLayoutComponent: () => ListLayoutMultipleComponent,
          getName: () => 'ProductGrid',
          getSkeletonLayout: () => 'grid'
        }
      ];
    } else {
      return [
        {
          getIconClass: () => 'fa fa-th',
          getListItemComponent: () => ProductGridViewV2Component,
          getListLayoutComponent: () => ListLayoutMultipleComponent,
          getName: () => 'ProductGrid',
          getSkeletonLayout: () => 'grid'
        },
        {
          getIconClass: () => 'fa fa-list',
          getListItemComponent: () => ProductListViewV2Component,
          getListLayoutComponent: () => ListLayoutMultipleComponent,
          getName: () => 'ProductList',
          getSkeletonLayout: () => 'table'
        }
      ];
    }
  }

  getProductGridCardComponent(item: GridViewProductCard): any {
    if (item.product.productType === 'concept') {
      return ConceptGridCardV2Component;
    } else {
      return ProductGridCardV2Component;
    }
  }

  getItems(): Observable<Product[]> {
    return this.assortmentSubject.pipe(
      switchMap((assortment: Assortment) => {
        if (assortment == null || !assortment.id) {
          return this.authService.getCurrentUser().pipe(
            filter(user => !!user),
            switchMap(user =>
              this.assortmentService.getPimAssortmentData(user.assortment).pipe(
                // Backends without PIM assortment data reject this endpoint — fall back so the list still loads
                catchError(() => of({ id: user.assortment, name: user.assortment, extra: {} } as Assortment))
              )
            ),
            tap(userAssortment => (this.currentAssortmentService.currentAssortment = userAssortment)),
            switchMap(assortmentData => this.fetchProducts(assortmentData))
          );
        }
        return this.fetchProducts(assortment);
      })
    );
  }

  protected fetchProducts(assortment: Assortment): Observable<Product[]> {
    this.dynamicColumns = this.getDynamicColumnsV2(assortment);

    let productObservable: Observable<Product[]>;
    if (this.fetchProductsExpanded === true) {
      productObservable = this.productService.getProductsByAssortmentExpanded(assortment.id).pipe(
        map(products => {
          return this.productSortService.sortProducts(products);
        })
      );
    } else {
      productObservable = this.productService.getProductsByAssortment(assortment.id).pipe(
        map(products => {
          return this.productSortService.sortProducts(products);
        })
      );
    }
    return productObservable.pipe(
      map(products => {
        if (this.dynamicColumns && this.dynamicColumns.length > 0) {
          products.map((product: ProductExtended) => {
            const dynamicCollumnsValues: string[] = [];
            for (const column of this.dynamicColumns) {
              if (column.isDisplay) {
                const paths: string[] = column.path.split('.');
                const value = (product.extra[paths[0]] && product.extra[paths[0]][paths[1]]) || '';
                if (
                  value?.translateProperties &&
                  Object.keys(value.translateProperties).length > 0 &&
                  value.translateProperties[this.currentLang] &&
                  value.translateProperties[this.currentLang].translatedDescription
                ) {
                  dynamicCollumnsValues.push(value.translateProperties[this.currentLang].translatedDescription);
                } else if (value && value.description) {
                  dynamicCollumnsValues.push(value.description);
                } else if (Array.isArray(value)) {
                  dynamicCollumnsValues.push(value.map(v => v.description).join(', '));
                } else if (column.type === 'metadata') {
                  if (column.metaReference) {
                    dynamicCollumnsValues.push(
                      this.metadataService.getMetadataValue(
                        column.metaReference.table,
                        column.metaReference.field,
                        value
                      )
                    );
                  } else if (column.metadata && column.metadata.split('.').length === 2) {
                    const splitmeta = column.metadata.split('.');
                    const metaTable = splitmeta[0];
                    const metaField = splitmeta[1];
                    dynamicCollumnsValues.push(this.metadataService.getMetadataValue(metaTable, metaField, value));
                  } else {
                    dynamicCollumnsValues.push(
                      this.metadataService.getMetadataValue(column.metadata, 'description', value)
                    );
                  }
                } else {
                  dynamicCollumnsValues.push(value || '');
                }
              }
            }
            product.dynamicCollumnsValues = dynamicCollumnsValues;
          });
        }
        return products;
      }),
      map(products => {
        if (assortment.extra?.pim?.filterConceptIncludedProducts) {
          let productsInConcepts = [];
          for (const product of products) {
            if (product.productType === 'concept') {
              productsInConcepts = [...(product as ProductConcept).subProductIds, ...productsInConcepts];
            }
          }
          return products.filter(p => !productsInConcepts.includes(p.id));
        }
        return products;
      })
    );
  }

  getDynamicColumnsV2(assortment: Assortment): PimTemplateProperties[] {
    const headerDynamicTranslationKeys: PimTemplateProperties[] = [];
    // Dynamic column
    if (assortment?.extra?.skuProperties || assortment?.extra?.itemProperties) {
      for (const skuProperty of (assortment.extra.skuProperties ||
        assortment.extra.itemProperties) as PimTemplateProperties[]) {
        if (skuProperty.isDisplay || skuProperty.isDisplayGrid) {
          headerDynamicTranslationKeys.push(skuProperty);
        }
      }
    }
    return headerDynamicTranslationKeys;
  }

  getDynamicColumns?(route?: ActivatedRouteSnapshot): PimTemplateProperties[] {
    return this.dynamicColumns;
  }

  getItemId(item: Product) {
    return item.id;
  }

  getSearchTerms(item: Product) {
    return [
      item.id,
      item.name,
      item.extra?.gme?.displayId,
      item.extra?.gme?.displayName,
      ...(item.extra?.pim?.keywords?.split(';') || [])
    ];
  }

  getCurrentFlow(): Observable<any> {
    return this.gungFlowService.getSelectedFlow();
  }

  getAssortmentBannerStyle(assortment: Assortment) {
    if (assortment?.extra?.images?.length > 0) {
      return {
        // eslint-disable-next-line quote-props
        height: '200px',
        // eslint-disable-next-line quote-props
        width: '100%',
        'background-size': 'cover',
        'background-position': 'center',
        'margin-bottom': '10px',
        'background-image':
          'url("' +
          this.gungImageUrlService.getUrl(assortment?.extra?.images?.[0]?.s3Uri, {
            etag: assortment?.extra?.images?.[0]?.s3ETag,
            type: GungImageType.high
          }) +
          '")'
      };
    }
  }

  showAssortmentBannerText(): boolean {
    return true;
  }

  getAddQueryParameterLimitToUrl(): boolean {
    return true;
  }
}
